import { t } from 'i18next';

export enum UserAccessErrorReason {
  LOCATION = 'location',
  AUTHENTICATION = 'authentication',
  ENTITLEMENT = 'entitlement',
  DEVICE_CONCURRENCY = 'device-concurrency',
}

export enum AuthorizationErrorReason {
  MISSING_TOKEN = 'missing-token',
  INVALID_TOKEN = 'invalid-token',
}

// for fatal errors, we should display a (somewhat readable) error code, so people can look it up to find more detailed explanations.
// if you add a new one here, please add it on the "Error Codes" confluence page too.
export enum ErrorCode {
  NoMenu = 'NO_MENU',
  NoHome = 'NO_HOME',
}

export class GeoRestrictionError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = t('app.geoRestriction.title');
    this.message = t('app.geoRestriction.message');
  }
}

export class PlaybackError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = t('error.generic.title');

    if (!this.message) {
      this.message = t('error.playbackStream.message');
    }
  }
}

/** A wrapper around Error with more details about failed response. It is enriched on the error interceptor level */
export class ApiError extends Error {
  details: any;
  statusCode: number;
  constructor(message?: string, details?: any) {
    super(message);
    this.details = details;
    this.statusCode = details?.response?.status ?? 400;
  }
}

export class UserAccessError extends Error {
  static readonly key: string = 'x-forbidden-reason';
  reason: UserAccessErrorReason;

  private getTranslations = (reason: UserAccessErrorReason) => {
    const getPair = (keyPrefix: string) => ({
      name: t(`${keyPrefix}.title`),
      message: t(`${keyPrefix}.message`),
    });

    switch (reason) {
      case UserAccessErrorReason.AUTHENTICATION:
        return getPair('error.authenticationRequired');
      case UserAccessErrorReason.ENTITLEMENT:
        return getPair('error.entitlementRequired');
      case UserAccessErrorReason.DEVICE_CONCURRENCY:
        return getPair('error.playbackConcurrency');
      case UserAccessErrorReason.LOCATION:
        return getPair('app.geoRestriction');
      default:
        return {
          name: t('web.app.accessDenied'),
          message: reason,
        };
    }
  };

  constructor(message: string, reason: UserAccessErrorReason) {
    super(message);
    this.reason = reason;
    const translations = this.getTranslations(reason);
    this.name = translations.name;
    this.message = translations.message;
  }
}

export class AuthorizationError extends Error {
  static readonly reason: string = 'x-authorization-error';
  reason: AuthorizationErrorReason;

  constructor(message: string, reason: AuthorizationErrorReason) {
    super(message);
    this.reason = reason;
  }
}

/** Determines whether the response indicates user access denial. */
export const isUserAccessResponse = (res: Response) => {
  const reason = res.headers?.get(UserAccessError.key);
  const isValidReason =
    reason &&
    Object.values(UserAccessErrorReason).includes(
      reason as UserAccessErrorReason
    );

  return res.status === 403 && isValidReason;
};

/** Determines whether the cause of an error was user access denial. */
export const isUserAccessError = (
  error?: Error,
  withReason?: UserAccessErrorReason
) => {
  return (
    error instanceof UserAccessError &&
    (!withReason || (error as UserAccessError).reason === withReason)
  );
};

/** Determines whether the cause of an error was geo-restriction. */
export const isGeoRestrictionError = (error?: Error) => {
  return error instanceof GeoRestrictionError;
};

export const isAuthorizationError = (error: Error) => {
  return error instanceof AuthorizationError;
};

export const isPlaybackError = (error?: Error) => {
  return error instanceof PlaybackError;
};
